import { BudgetRequestListItemDto } from '../../../types';
import {
  ExportColumn,
  ExportFilterValue,
  ExportRow,
  ExportSheet,
  formatExportDate,
} from '../../../lib/export';

const CURRENCY_FMT = '"$"#,##0.00';

export const BUDGET_EXPORT_COLUMNS: ExportColumn[] = [
  { key: 'itemExpense', header: 'Item / Expense', width: 40 },
  { key: 'brgName', header: 'BRG', width: 26 },
  { key: 'fiscalYear', header: 'Fiscal Year', width: 14 },
  { key: 'projectEvent', header: 'Project / Event', width: 26 },
  { key: 'requestedDate', header: 'Requested Date', width: 16 },
  { key: 'requestedBy', header: 'Requested By', width: 22 },
  { key: 'status', header: 'Status', width: 16 },
  { key: 'requestAmount', header: 'Request Amount', width: 16, numFmt: CURRENCY_FMT },
  { key: 'approvedAmount', header: 'Approved Amount', width: 16, numFmt: CURRENCY_FMT },
  { key: 'spentAmount', header: 'Spent Amount', width: 16, numFmt: CURRENCY_FMT },
  { key: 'remainingAmount', header: 'Remaining', width: 16, numFmt: CURRENCY_FMT },
  { key: 'scheduledDate', header: 'Scheduled Date', width: 16 },
  { key: 'requestDescription', header: 'Description', width: 48 },
];

export interface BudgetExportLookups {
  brgNameById: Map<number, string>;
  statusNameById: Map<number, string>;
  userNameById: Map<number, string>;
}

function num(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function buildBudgetExportRows(
  budgets: BudgetRequestListItemDto[],
  lookups: BudgetExportLookups
): ExportRow[] {
  return budgets.map((b) => {
    const approved = num(b.approvedAmount);
    const spent = num(b.spentAmount);
    return {
      itemExpense: b.purposeDescription || 'Untitled',
      brgName: lookups.brgNameById.get(b.brgId) ?? `BRG ${b.brgId}`,
      fiscalYear: b.fiscalYear || '—',
      projectEvent: b.projectEvent || '—',
      requestedDate: formatExportDate(b.requestedDate),
      requestedBy: b.requestedById
        ? lookups.userNameById.get(b.requestedById) ?? `User #${b.requestedById}`
        : '—',
      status: lookups.statusNameById.get(b.budgetStatusId) ?? 'Unknown',
      requestAmount: num(b.requestAmount),
      approvedAmount: approved,
      spentAmount: spent,
      // Approved budget is the meaningful baseline once a request is approved;
      // fall back to the requested amount while it is still pending.
      remainingAmount: (approved > 0 ? approved : num(b.requestAmount)) - spent,
      scheduledDate: formatExportDate(b.scheduledDate),
      requestDescription: b.requestDescription || '',
    };
  });
}

export interface BudgetFilterState {
  fiscalYear: string;
  selectedBrgId: number | null;
  searchQuery: string;
  brgNameById: Map<number, string>;
}

export function buildBudgetExportFilters(state: BudgetFilterState): ExportFilterValue[] {
  return [
    { label: 'Fiscal Year', value: state.fiscalYear || 'All' },
    {
      label: 'BRG',
      value:
        state.selectedBrgId == null
          ? 'All BRGs'
          : state.brgNameById.get(state.selectedBrgId) ?? `BRG ${state.selectedBrgId}`,
    },
    { label: 'Search', value: state.searchQuery.trim() || 'None' },
  ];
}

export interface BudgetSummaryTotals {
  totalAllocated: number;
  totalSpent: number;
  remaining: number;
  utilizationPercent: number;
}

export function buildBudgetExportSheet(
  rows: ExportRow[],
  totals: BudgetSummaryTotals
): ExportSheet {
  return {
    name: 'Budget Requests',
    columns: BUDGET_EXPORT_COLUMNS,
    rows,
    summaryTitle: 'Summary',
    summary: [
      { label: 'Budget requests exported', value: rows.length },
      { label: 'Total allocated', value: totals.totalAllocated },
      { label: 'Total spent', value: totals.totalSpent },
      { label: 'Remaining', value: totals.remaining },
      { label: 'Utilization', value: `${totals.utilizationPercent}%` },
    ],
  };
}
